import { Croppy } from '../../../helpers/classes/Croppy';
import EventService from '../../../helpers/classes/EventService';
import LanguageService from '../../../helpers/classes/LanguageService';
import RenderingService from '../../../helpers/classes/RenderingService';
import TemplateService from '../../../helpers/classes/TemplateService';
import { removeNullOrUndefined } from '../../../helpers/functions/object';
import DataService, { DataStoreType } from '../../classes/DataService';
import { JourneyContainer } from '../../classes/JourneyContainer';
import { JourneyEvent } from '../../enums/JourneyEvent';
import { RequiredInputImage } from '../../enums/RequiredInputImage';
import { Templates } from '../../enums/Templates';
import { TranslationKey } from '../../enums/Translation';
import { IIdScanData } from '../../interfaces/DataFormats';
import { IExtendedDocument } from '../../interfaces/Document';
import { IPerson } from '../../interfaces/Person';
import { UploadResponse } from '../../interfaces/UploadResponse';

export class CropperProvider {
	public static render(this: JourneyContainer) {
		return TemplateService.getElements(this._container, {
			providerContainer: '[data-jcs-element="provider__container"]',
		})
			.then(({ providerContainer }) => TemplateService.renderToContainer(providerContainer, Templates.Cropper))
			.then(() => TemplateService.getElements(this._container, {
				toggle: '[data-jcs-element="gateway__upload__toggle"]',
				toggleLabel: '[data-jcs-element="gateway__upload__toggle__label"]',
				cropper: '[data-jcs-element="cropper__canvas"]',
				cropperSubmit: '[data-jcs-element="cropper__submit"]',
				cropperReset: '[data-jcs-element="cropper__retry"]',
				status: '[data-jcs-element="cropper__status"]',
				statusMessage: '[data-jcs-element="cropper__status__message"]',
			}))
			.then(({ cropper, cropperSubmit, cropperReset, status, statusMessage, toggle, toggleLabel }) => {
				if (cropper && this._cropResponse !== undefined) {
					cropper.id = 'jcs__cropper__canvas';

					RenderingService.hideObject(toggle);
					RenderingService.hideObject(toggleLabel);

					const croppy = new Croppy({
						imageSrc: 'data:image/jpeg;base64,' + this._cropResponse.outputImage,
						magnifierCanvasId: 'magnifier',
						padding: 20,
						showCornersAlways: true,
						targetCanvasId: `${cropper.id}`
					});

					if (cropperSubmit instanceof HTMLElement) {
						cropperSubmit.addEventListener('click', () => {
							RenderingService.setEnabled(cropperSubmit as HTMLInputElement, false);
							RenderingService.setEnabled(cropperReset as HTMLInputElement, false);

							RenderingService.showObject(status as HTMLElement);
							RenderingService.setText(statusMessage as HTMLElement, LanguageService.getTranslation(TranslationKey.CROPPER_UPLOADING));

							this.getJourney()
								.then((journey) => journey.continue({
									imgId: this._cropResponse!.id || '',
									imgList: [{
										data: this._cropResponse!.outputImage,
										name: this._journey!.action === 'SELFIE' ? RequiredInputImage.SelfiePhoto : RequiredInputImage.WhiteImage,
										inputFormat: 0
									}],
									coordinates: croppy.getOriginalSelectionAreaPoints().points,
									journeyDefinitionId: this._journeyDefinitionId
								}))
								.then((res) => DataService.getDataStore<IIdScanData>(DataStoreType.ID_SCAN).update(CropperProvider.parseResponseData(res)))
								.then(() => {
									this._cropResponse = undefined;
									this.evaluate();
								})
								.catch(() => {
									RenderingService.showObject(status as HTMLElement);
									RenderingService.setText(statusMessage as HTMLElement, LanguageService.getTranslation(TranslationKey.EXCEPTION_NETWORK));
								})
								.then(() => {
									RenderingService.setEnabled(cropperSubmit as HTMLInputElement, true);
									RenderingService.setEnabled(cropperReset as HTMLInputElement, true);
								});
						});
					}

					if (cropperReset instanceof HTMLElement) {
						cropperReset.addEventListener('click', () => {
							this._cropResponse = undefined;
							this.evaluate();
						});
					}

					croppy.init(this._cropResponse.isDetected ? this._cropResponse.coordinates : [], true);
				}

			})
			.then(() => EventService.publish(JourneyEvent.DISPLAY_PROVIDER, { provider: Templates.Cropper }));
	}

	private static parseResponseData = ({
		entryData,
		entryDateTime,
		journeyId,
		requiredAction,
		highLevelResult,
		currentResult,
		hasError,
		isFinished
	}: UploadResponse): Partial<IIdScanData> => ({
		journeyId,
		documents: (entryData.documents || []).map((doc): IExtendedDocument => removeNullOrUndefined({
			expirationDate: doc.expirationDate,
			issueDate: doc.issueDate,
			issueNumber: doc.issueNumber,
			number: doc.number,
			type: doc.type,
			blurCheck: entryData.blurCheck === 'Good',
			glareCheck: entryData.blurCheck === 'Good',
			lowResolutionCheck: entryData.lowResolutionCheck === 'Good',
			scanReference: entryData.scanReference,
			scannerIp: entryData.scannerIp,
			dateTime: entryDateTime ? new Date(entryDateTime) : undefined
		})),
		person: removeNullOrUndefined({
			address: {
				city: entryData.holder.address.city,
				full: entryData.holder.address.full,
				postCode: entryData.holder.address.postCode,
			},
			birthDate: entryData.holder.birthDate,
			birthPlace: entryData.holder.birthPlace,
			firstName: entryData.holder.firstName,
			fullName: entryData.holder.fullName,
			middleName: entryData.holder.middleName,
			lastName: entryData.holder.lastName,
			gender: entryData.holder.gender,
		}) as IPerson,
		requiredAction,
		highLevelResult,
		currentResult,
		hasError,
		isFinished
	})
}
